/**
 * Freedom Fuel — Cart Page
 *
 * Wires the custom cart page (sections/main-cart.liquid):
 *   1. Qty steppers / remove via /cart/change.js
 *   2. Live ship-strip + summary recalculation
 *   3. Hero vote allocator (3-column grid) sharing localStorage and
 *      cart attribute ff_hero_allocations with the cart drawer.
 *   4. "Updated XX ago" ticker
 */

const VOTING_API = 'https://voting.freedomfuel.us';
const CART_ATTR  = 'ff_hero_allocations';
const LS_KEY     = 'ff_hero_allocations';
const ACCENTS    = ['#3A3326', '#2E1F1B', '#1B2A3A', '#2A1F2E', '#3A2820', '#1F2E2A'];

(function () {
  const root = document.querySelector('[data-ffcp-root]');
  if (!root) return;

  const $puckStrip = root.querySelector('[data-ffcp-puckstrip]');

  const state = {
    cartTotalCents: parseInt(root.dataset.cartTotal || 0, 10),
    shipThreshold:  parseInt(root.dataset.shipThreshold || 4000, 10),
    flatShip:       parseInt(root.dataset.flatShipCents || 500, 10),
    impactPct:      parseFloat(root.dataset.impactPct || 5),
    puckQty:        parseInt($puckStrip?.dataset.puckQty || 0, 10),
    puckThreshold:  parseInt($puckStrip?.dataset.puckThreshold || 3, 10),
    earnedVotes:    0,
    allocations:    {},
    heroes:         [],
    heroesFetched:  false,
  };

  const $votes = root.querySelector('[data-ffcp-votes]');
  if ($votes) state.earnedVotes = parseInt($votes.dataset.orderVotes || 0, 10);
  restoreAllocations();
  clampAllocations();

  /* ── INIT ── */
  bindQty();
  bindAlloc();
  updateShipStrip();
  updatePuckStrip();
  updateSummary();
  renderAllocPanel();
  startTicker();

  if ($votes) {
    fetchHeroes().then(renderHeroes);
  }

  /* ───────── QTY / REMOVE ───────── */
  function bindQty() {
    root.addEventListener('click', e => {
      const incDec = e.target.closest('[data-ffcp-qty]');
      if (incDec) {
        e.preventDefault();
        const item = incDec.closest('[data-ffcp-item]');
        const numEl = item.querySelector('[data-ffcp-qty-num]');
        const cur   = parseInt(numEl.textContent, 10) || 0;
        const next  = incDec.dataset.ffcpQty === 'inc' ? cur + 1 : Math.max(0, cur - 1);
        updateLine(item, next);
        return;
      }
      const remove = e.target.closest('[data-ffcp-remove]');
      if (remove) {
        e.preventDefault();
        const item = remove.closest('[data-ffcp-item]');
        updateLine(item, 0);
        return;
      }
    });
  }

  function updateLine(item, quantity) {
    const key = item.dataset.lineKey;
    item.classList.add('is-removing');
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    })
      .then(r => r.json())
      .then(cart => {
        if (quantity === 0) {
          item.remove();
        } else {
          item.classList.remove('is-removing');
          const numEl = item.querySelector('[data-ffcp-qty-num]');
          if (numEl) numEl.textContent = quantity;
          const line = cart.items.find(i => i.key === key);
          if (line) {
            const total = item.querySelector('[data-ffcp-line-total]');
            if (total) total.textContent = formatMoney(line.final_line_price);
          }
        }
        applyCart(cart);
        if (cart.item_count === 0) location.reload();
      })
      .catch(err => {
        console.warn('[ffcp] cart update failed', err);
        item.classList.remove('is-removing');
      });
  }

  function applyCart(cart) {
    state.cartTotalCents = cart.items_subtotal_price;
    state.puckQty = cart.item_count;
    state.earnedVotes = cart.items.reduce((sum, it) => {
      const v = parseInt(it?.product?.metafields?.freedom_fuel_voting?.vote_value || 0, 10) || 0;
      return sum + v * it.quantity;
    }, 0);
    if ($votes) {
      $votes.dataset.orderVotes = state.earnedVotes;
      $votes.querySelectorAll('[data-ff-earned-votes]').forEach(el => el.textContent = state.earnedVotes);
      const ofEl = $votes.querySelector('.alloc .num-block .of');
      if (ofEl) ofEl.textContent = `/ ${state.earnedVotes}`;
    }
    const earnedHead = root.querySelector('[data-ffcp-earned]');
    if (earnedHead) earnedHead.textContent = state.earnedVotes;
    const cartCount = root.querySelector('[data-ffcp-cart-count]');
    if (cartCount) cartCount.textContent = cart.item_count;

    clampAllocations();
    updateShipStrip();
    updatePuckStrip();
    updateSummary(cart);
    renderAllocPanel();
    renderHeroes();
    bump('Updated 00:00 ago');

    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  }

  /* ───────── SHIP STRIP ───────── */
  function updateShipStrip() {
    const strip = root.querySelector('[data-ffcp-shipstrip]');
    if (!strip) return;
    const total = state.cartTotalCents;
    const thr   = state.shipThreshold;
    const pct   = Math.min(100, Math.round((total / thr) * 100));
    const remaining = Math.max(0, thr - total);
    const unlocked  = remaining === 0;

    const fill = strip.querySelector('[data-ffcp-ship-fill]');
    const text = strip.querySelector('[data-ffcp-ship-text]');
    const num  = strip.querySelector('[data-ffcp-ship-num]');
    const sub  = strip.querySelector('[data-ffcp-ship-sub]');

    if (fill) {
      fill.style.width = pct + '%';
      fill.classList.toggle('unlocked', unlocked);
    }
    if (text) {
      text.textContent = unlocked ? 'Free Shipping · Unlocked' : `${formatMoney(remaining)} to Free Shipping`;
      text.classList.toggle('unlocked', unlocked);
    }
    if (num) {
      num.textContent = unlocked ? 'CLEAR' : formatMoney(remaining);
      num.classList.toggle('unlocked', unlocked);
    }
    if (sub) {
      sub.textContent = unlocked ? "You're set · ships within 24h" : 'Stack to unlock';
    }
    strip.querySelectorAll('[data-ffcp-ship-tick]').forEach(t => {
      const at = parseInt(t.dataset.ffcpShipTick, 10);
      t.classList.toggle('reached', total >= at);
      if (unlocked && at >= thr) t.classList.add('unlocked');
      else t.classList.remove('unlocked');
    });
  }

  /* ───────── PUCK STRIP ───────── */
  function updatePuckStrip() {
    if (!$puckStrip) return;
    const qty      = state.puckQty;
    const thr      = state.puckThreshold;
    const remaining = Math.max(0, thr - qty);
    const unlocked  = qty >= thr;
    const pct       = Math.min(100, Math.round((qty / thr) * 100));

    const fill = $puckStrip.querySelector('[data-ffcp-puck-fill]');
    const text = $puckStrip.querySelector('[data-ffcp-puck-text]');
    const num  = $puckStrip.querySelector('[data-ffcp-puck-num]');
    const sub  = $puckStrip.querySelector('[data-ffcp-puck-sub]');

    if (fill) {
      fill.style.width = pct + '%';
      fill.classList.toggle('unlocked', unlocked);
    }
    if (text) {
      text.textContent = unlocked
        ? 'Free Puck · Unlocked'
        : `Add ${remaining} more puck${remaining === 1 ? '' : 's'} to unlock a free puck`;
      text.classList.toggle('unlocked', unlocked);
    }
    if (num) {
      num.textContent = unlocked ? 'FREE' : `+${remaining}`;
      num.classList.toggle('unlocked', unlocked);
    }
    if (sub) {
      sub.textContent = unlocked ? 'Ships with your order · no code needed' : 'Offer ends July 6';
    }
    $puckStrip.querySelectorAll('[data-ffcp-puck-tick]').forEach(t => {
      const at = parseInt(t.dataset.ffcpPuckTick, 10);
      t.classList.toggle('reached', qty >= at);
      t.classList.toggle('unlocked', unlocked && qty >= at);
    });

    const freeLine = root.querySelector('[data-ffcp-free-puck-line]');
    if (freeLine) {
      freeLine.classList.toggle('is-unlocked', unlocked);
      freeLine.classList.toggle('is-locked', !unlocked);
    }
    const freeV = root.querySelector('[data-ffcp-free-puck-v]');
    if (freeV) {
      freeV.textContent = unlocked
        ? 'FREE'
        : `${remaining} more puck${remaining === 1 ? '' : 's'}`;
    }
  }

  /* ───────── SUMMARY ───────── */
  function updateSummary(cart) {
    const subEl    = root.querySelector('[data-ffcp-subtotal]');
    const shipEl   = root.querySelector('[data-ffcp-ship-v]');
    const impactEl = root.querySelector('[data-ffcp-impact-v]');
    const totalEl  = root.querySelector('[data-ffcp-total]');

    const sub    = state.cartTotalCents;
    const ship   = sub >= state.shipThreshold ? 0 : state.flatShip;
    const impact = Math.round(sub * (state.impactPct / 100));

    if (subEl)    subEl.textContent = formatMoney(sub);
    if (shipEl)   shipEl.textContent = ship === 0 ? 'FREE' : formatMoney(ship);
    if (impactEl) impactEl.textContent = formatMoney(impact);
    if (totalEl && cart) {
      totalEl.innerHTML = `${formatMoney(cart.total_price)}<small>USD · est.</small>`;
    } else if (totalEl) {
      totalEl.innerHTML = `${formatMoney(sub + ship)}<small>USD · est.</small>`;
    }
  }

  /* ───────── HEROES ───────── */
  async function fetchHeroes() {
    try {
      const res  = await fetch(`${VOTING_API}/api/storefront/heroes`, { mode: 'cors' });
      const json = await res.json();
      state.heroes = json.heroes || [];
      state.heroesFetched = true;
    } catch (err) {
      console.warn('[ffcp] fetchHeroes failed', err);
      state.heroes = [];
      state.heroesFetched = true;
    }
  }

  function renderHeroes() {
    const grid = root.querySelector('[data-ffcp-heroes]');
    if (!grid) return;
    if (!state.heroesFetched) {
      grid.innerHTML = '<div class="ffcp-heroes__loading">Loading heroes…</div>';
      return;
    }
    if (!state.heroes.length) {
      grid.innerHTML = '<div class="ffcp-heroes__loading">No nominees available.</div>';
      return;
    }
    grid.innerHTML = state.heroes.map((h, i) => renderCard(h, i)).join('');
  }

  function renderCard(hero, idx) {
    const id        = String(hero.id || '');
    const name      = esc(hero.displayName || 'Hero');
    const handle    = esc(hero.handle || hero.tagline || '');
    const votes     = Number(hero.votesCast || 0);
    const goal      = Number(hero.voteGoal || hero.goalVotes || Math.max(votes * 2, 5000));
    const photo     = hero.image || hero.imageUrl || '';
    const accent    = ACCENTS[idx % ACCENTS.length];
    const initials  = (hero.displayName || '?').split(/\s+/).map(p => p[0]).slice(0, 2).join('').toUpperCase();
    const fileNo    = String(hero.fileNo || idx + 1).padStart(3, '0');
    const share     = goal > 0 ? Math.min(100, (votes / goal) * 100) : 0;
    const allocated = state.allocations[id] || 0;

    const artHtml = photo
      ? `<div class="art art--photo" style="background-image:url('${esc(photo)}'); --accent:${accent}"></div>`
      : `<div class="art" style="--accent:${accent}"></div><div class="ring">${esc(initials)}</div>`;

    return `
<div class="ffcp-hero${allocated > 0 ? ' is-allocated' : ''}" data-hero-id="${esc(id)}">
  <div class="portrait">
    <div class="file-tag">#${fileNo}</div>
    ${artHtml}
  </div>
  <div class="body">
    <div class="name">${name}${handle ? `<small>${handle}</small>` : ''}</div>
    <div class="progress">
      <div class="row">
        <span>Votes</span>
        <span class="v">${votes.toLocaleString()} <span style="color:#6B6B6B">/ ${formatThousand(goal)}</span></span>
      </div>
      <div class="track"><div class="fill" style="width:${share}%"></div></div>
    </div>
    <div class="stepper" data-hid="${esc(id)}">
      <button type="button" data-act="dec" ${allocated === 0 ? 'disabled' : ''} aria-label="Decrease">−</button>
      <span class="v ${allocated > 0 ? 'has' : ''}">${allocated === 0 ? '+ Vote' : `+${allocated} Vote${allocated > 1 ? 's' : ''}`}</span>
      <button type="button" data-act="inc" aria-label="Increase">+</button>
    </div>
  </div>
</div>`.trim();
  }

  /* ───────── ALLOCATION ───────── */
  function bindAlloc() {
    root.addEventListener('click', e => {
      const stepBtn = e.target.closest('.ffcp-hero .stepper button');
      if (stepBtn) {
        const hid = stepBtn.closest('.stepper')?.dataset.hid;
        if (!hid) return;
        if (stepBtn.dataset.act === 'inc') incAlloc(hid);
        else decAlloc(hid);
        return;
      }
      if (e.target.closest('[data-ff-alloc-reset]')) {
        state.allocations = {};
        persist();
        renderHeroes();
        renderAllocPanel();
        return;
      }
      if (e.target.closest('[data-ffcp-split-even]')) {
        splitEven();
        return;
      }
    });
  }

  function incAlloc(hid) {
    if (totalAlloc() >= state.earnedVotes) return;
    state.allocations[hid] = (state.allocations[hid] || 0) + 1;
    persist();
    patchCard(hid);
    renderAllocPanel();
  }
  function decAlloc(hid) {
    const cur = state.allocations[hid] || 0;
    if (cur <= 0) return;
    if (cur - 1 === 0) delete state.allocations[hid];
    else state.allocations[hid] = cur - 1;
    persist();
    patchCard(hid);
    renderAllocPanel();
  }
  function splitEven() {
    if (!state.heroes.length || !state.earnedVotes) return;
    const n = state.heroes.length;
    const base = Math.floor(state.earnedVotes / n);
    let extra = state.earnedVotes - base * n;
    state.allocations = {};
    state.heroes.forEach(h => {
      const id = String(h.id);
      const v = base + (extra > 0 ? 1 : 0);
      if (v > 0) state.allocations[id] = v;
      if (extra > 0) extra--;
    });
    persist();
    renderHeroes();
    renderAllocPanel();
  }
  function totalAlloc() {
    return Object.values(state.allocations).reduce((s, n) => s + n, 0);
  }
  function clampAllocations() {
    const total = totalAlloc();
    if (total <= state.earnedVotes) return;
    if (state.earnedVotes === 0) { state.allocations = {}; persist(); return; }
    let overflow = total - state.earnedVotes;
    const ids = Object.keys(state.allocations);
    while (overflow > 0 && ids.length) {
      for (const id of ids) {
        if (overflow <= 0) break;
        if ((state.allocations[id] || 0) > 0) {
          state.allocations[id] -= 1;
          overflow -= 1;
          if (state.allocations[id] === 0) delete state.allocations[id];
        }
      }
    }
    persist();
  }
  function patchCard(hid) {
    const card = root.querySelector(`.ffcp-hero[data-hero-id="${cssEscape(hid)}"]`);
    if (!card) return;
    const allocated = state.allocations[hid] || 0;
    card.classList.toggle('is-allocated', allocated > 0);
    const v = card.querySelector('.stepper .v');
    const dec = card.querySelector('[data-act="dec"]');
    if (v) {
      v.textContent = allocated === 0 ? '+ Vote' : `+${allocated} Vote${allocated > 1 ? 's' : ''}`;
      v.classList.toggle('has', allocated > 0);
    }
    if (dec) dec.disabled = allocated === 0;
  }
  function renderAllocPanel() {
    const committed = totalAlloc();
    const remaining = Math.max(0, state.earnedVotes - committed);
    const pct       = state.earnedVotes > 0 ? (committed / state.earnedVotes) * 100 : 0;
    root.querySelectorAll('[data-ff-alloc-committed]').forEach(el => el.textContent = committed);
    root.querySelectorAll('[data-ff-alloc-remaining]').forEach(el => el.textContent = remaining);
    root.querySelectorAll('[data-ff-alloc-meter]').forEach(el => el.style.width = pct + '%');
    root.querySelectorAll('[data-ffcp-alloc-pct]').forEach(el => el.textContent = pct.toFixed(0) + '%');
    root.querySelectorAll('[data-ffcp-vote-seal]').forEach(el => el.textContent = committed);
  }
  function persist() {
    try { localStorage.setItem(LS_KEY, JSON.stringify(state.allocations)); } catch (_) {}
    clearTimeout(persist._t);
    persist._t = setTimeout(() => {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { [CART_ATTR]: JSON.stringify(state.allocations) } }),
      }).catch(err => console.warn('[ffcp] alloc write failed', err));
    }, 350);
  }
  function restoreAllocations() {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) state.allocations = JSON.parse(raw) || {};
    } catch (_) { state.allocations = {}; }
  }

  /* ───────── TICKER ───────── */
  function startTicker() {
    let sec = 0;
    setInterval(() => {
      sec++;
      const m = Math.floor(sec / 60), s = sec % 60;
      const t = `Updated ${m > 0 ? String(m).padStart(2, '0') + ':' : '00:'}${String(s).padStart(2, '0')} ago`;
      bump(t);
    }, 1000);
  }
  function bump(text) {
    root.querySelectorAll('[data-ffcp-updated]').forEach(el => el.textContent = text);
    root.querySelectorAll('[data-ffcp-updated-b]').forEach(el => el.textContent = text + ' · auto-saved');
  }

  /* ───────── UTIL ───────── */
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }
  function formatThousand(n) {
    return n >= 1000 ? (n / 1000).toFixed(0) + 'k' : String(n);
  }
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  }
  function cssEscape(s) {
    return String(s).replace(/(["\\\]\[])/g, '\\$1');
  }
})();
